import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import { CircleNotch } from "@phosphor-icons/react";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Leaderboards from "./pages/Leaderboards";
import Connection from "./pages/Connection";
import DataViewer from "./pages/DataViewer";
import AssetUpdater from "./pages/AssetUpdater";
import AssetList from "./pages/AssetList";
import AssetDetail from "./pages/AssetDetail";
import AssetIntelligence from "./pages/AssetIntelligence";
import AssetIntelligenceLive from "./pages/AssetIntelligenceLive";
import Scheduler from "./pages/Scheduler";
import ClientsList from "./pages/ClientsList";
import ClientDetail from "./pages/ClientDetail";
import JobDetail from "./pages/JobDetail";
import QuoteDetail from "./pages/QuoteDetail";
import ClientGroups from "./pages/ClientGroups";
import ClientGroupDetail from "./pages/ClientGroupDetail";
import AdminUsers from "./pages/AdminUsers";
import AroFloOptions from "./pages/AroFloOptions";
import LegacyApi from "./pages/LegacyApi";
import LegacyZone from "./pages/LegacyZone";
import MaximoIntegration from "./pages/MaximoIntegration";
import MexIntegration from "./pages/MexIntegration";
import MaximoWorkOrderDetail from "./pages/MaximoWorkOrderDetail";
import CoverPages from "./pages/CoverPages";
import SiteSettings from "./pages/SiteSettings";
import CoverSplash from "./components/CoverSplash";
import { api } from "./lib/api";
import { loadAndApplySiteMeta } from "./lib/siteMeta";
import { useEffect, useState } from "react";
import Technicians from "./pages/Technicians";
import Jobs from "./pages/Jobs";
import TechnicianDetail from "./pages/TechnicianDetail";
import Timesheets from "./pages/Timesheets";
import ClientHome from "./pages/ClientHome";
import Security from "./pages/Security";
import Reports from "./pages/Reports";
import ReportView from "./pages/ReportView";
import Logs from "./pages/Logs";
import AssetIntelligenceV2Admin from "./pages/AssetIntelligenceV2Admin";
import AssetIntelligenceV2Client from "./pages/AssetIntelligenceV2Client";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Sends each role to its landing page.
function RoleHome() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB]" data-testid="role-home-loading">
        <CircleNotch size={32} className="animate-spin text-[#0055FF]" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/dashboard" : "/home"} replace />;
}

// Renders the role-appropriate Asset Intelligence V2 (Sample) experience.
function AssetIntelligenceV2Switch() {
  const { user } = useAuth();
  return user?.role === "admin" ? <AssetIntelligenceV2Admin /> : <AssetIntelligenceV2Client />;
}

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth return BEFORE any routing/auth check (race-condition safe).
  // Accept the session id from the URL fragment or query string.
  if (location.hash?.includes("session_id=") || location.search?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <CoverGate>
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/home" element={<ProtectedRoute><ClientHome /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute requireAdmin><Dashboard /></ProtectedRoute>} />
      <Route path="/leaderboard/:metric" element={<ProtectedRoute requireAdmin><Leaderboards /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/reports/:id" element={<ProtectedRoute><ReportView /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute requireAdmin><ClientsList /></ProtectedRoute>} />
      <Route path="/client-groups" element={<ProtectedRoute requireAdmin><ClientGroups /></ProtectedRoute>} />
      <Route path="/client-groups/:id" element={<ProtectedRoute requireAdmin><ClientGroupDetail /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><ClientDetail /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute requireAdmin><Jobs /></ProtectedRoute>} />
      <Route path="/technicians" element={<ProtectedRoute requireAdmin><Technicians /></ProtectedRoute>} />
      <Route path="/technicians/:id" element={<ProtectedRoute requireAdmin><TechnicianDetail /></ProtectedRoute>} />
      <Route path="/timesheets" element={<ProtectedRoute requireAdmin><Timesheets /></ProtectedRoute>} />
      <Route path="/jobs/:id" element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
      <Route path="/logs" element={<ProtectedRoute requireAdmin><Logs /></ProtectedRoute>} />
      <Route path="/asset-intelligence-v2" element={<ProtectedRoute><AssetIntelligenceV2Switch /></ProtectedRoute>} />
      <Route path="/quotes/:id" element={<ProtectedRoute><QuoteDetail /></ProtectedRoute>} />
      <Route path="/connection" element={<ProtectedRoute requireAdmin><Connection /></ProtectedRoute>} />
      <Route path="/aroflo-options" element={<ProtectedRoute requireAdmin><AroFloOptions /></ProtectedRoute>} />
      <Route path="/legacy-api" element={<ProtectedRoute requireAdmin><LegacyApi /></ProtectedRoute>} />
      <Route path="/legacy-api/:zone" element={<ProtectedRoute requireAdmin><LegacyZone /></ProtectedRoute>} />
      <Route path="/maximo" element={<ProtectedRoute requireAdmin><MaximoIntegration /></ProtectedRoute>} />
      <Route path="/mex" element={<ProtectedRoute requireAdmin><MexIntegration /></ProtectedRoute>} />
      <Route path="/maximo/work-orders/:wonum" element={<ProtectedRoute requireAdmin><MaximoWorkOrderDetail /></ProtectedRoute>} />
      <Route path="/cover" element={<ProtectedRoute requireAdmin><CoverPages /></ProtectedRoute>} />
      <Route path="/site-settings" element={<ProtectedRoute requireAdmin><SiteSettings /></ProtectedRoute>} />
      <Route path="/viewer" element={<ProtectedRoute requireAdmin><DataViewer /></ProtectedRoute>} />
      <Route path="/asset-updater" element={<ProtectedRoute requireAdmin><AssetUpdater /></ProtectedRoute>} />
      <Route path="/asset-list" element={<ProtectedRoute requireAdmin><AssetList /></ProtectedRoute>} />
      <Route path="/asset-detail" element={<ProtectedRoute requireAdmin><AssetDetail /></ProtectedRoute>} />
      <Route path="/scheduler" element={<ProtectedRoute requireAdmin><Scheduler /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute><AssetIntelligenceLive /></ProtectedRoute>} />
      <Route path="/intelligence-sample" element={<ProtectedRoute requireAdmin><AssetIntelligence /></ProtectedRoute>} />
      <Route path="/" element={<RoleHome />} />
        <Route path="*" element={<RoleHome />} />
      </Routes>
    </CoverGate>
  );
}

// Blocks public visitors with the splash when a cover mode is on. Admins always bypass.
function CoverGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [cover, setCover] = useState(undefined);

  useEffect(() => {
    let mounted = true;
    api.get("/cover/public").then((r) => mounted && setCover(r.data)).catch(() => mounted && setCover({ enabled: false }));
    return () => { mounted = false; };
  }, []);

  const Spinner = (
    <div className="flex min-h-screen items-center justify-center bg-[#F9FAFB]" data-testid="cover-gate-loading">
      <CircleNotch size={32} className="animate-spin text-[#0055FF]" />
    </div>
  );

  if (cover === undefined) return Spinner;
  if (!cover.enabled) return children;

  const isAdmin = user && user.role === "admin";
  if (isAdmin) return children;
  // Always allow the login route so admins can authenticate to bypass.
  if (location.pathname.startsWith("/login")) return children;
  if (user === null) return Spinner; // wait for auth to resolve before blocking
  return <CoverSplash content={cover.content} />;
}

function App() {
  useEffect(() => { loadAndApplySiteMeta(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
